class BrochuresDocuments {
  constructor(root) {
    this.root = root;
    this.shop = root.dataset.shop;
    this.token = (root.dataset.token || '').trim();
    this.apiVersion = (root.dataset.apiVersion || '2025-01').trim();
    this.metaobjectType = root.dataset.metaobjectType || 'documents';
    this.perPage = Number.parseInt(root.dataset.perPage, 10) || 20;
    this.pageParam = root.dataset.pageParam || 'page';
    this.searchParam = root.dataset.searchParam || 'q';
    this.emptyMessage = root.dataset.emptyMessage || 'No documents found.';
    this.errorMessage = root.dataset.errorMessage || 'Unable to load documents. Please try again later.';
    this.setupMessage = root.dataset.setupMessage || 'Add a Storefront API token in the section settings.';
    this.unassignedLabel = root.dataset.unassignedLabel || 'Other documents';
    this.previousLabel = root.dataset.previousLabel || 'Previous';
    this.nextLabel = root.dataset.nextLabel || 'Next';
    this.downloadLabel = root.dataset.downloadLabel || 'View document';
    this.resultsLabel = root.dataset.resultsLabel || 'products with documents';

    this.listEl = root.querySelector('[data-bd-list]');
    this.paginationEl = root.querySelector('[data-bd-pagination]');
    this.searchInput = root.querySelector('[data-bd-search]');
    this.statusEl = root.querySelector('[data-bd-status]');
    this.countEl = root.querySelector('[data-bd-count]');
    this.loadingEl = root.querySelector('[data-bd-loading]');

    this.documents = [];
    this.productGroups = [];
    this.filteredGroups = [];
    this.currentPage = 1;
    this.searchQuery = '';
    this.searchTimer = null;

    if (!this.shop || !this.token) {
      this.showStatus(this.setupMessage);
      this.hideLoading();
      return;
    }

    this.bindEvents();
    this.readUrlState();
    this.init();
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.searchQuery = this.searchInput.value.trim();
          this.currentPage = 1;
          this.applyFilters();
          this.writeUrlState();
        }, 250);
      });
    }

    this.root.addEventListener('click', (event) => {
      const pageButton = event.target.closest('[data-bd-page]');
      if (pageButton) {
        event.preventDefault();
        const page = Number.parseInt(pageButton.dataset.bdPage, 10);
        if (!Number.isNaN(page) && page !== this.currentPage) {
          this.currentPage = page;
          this.render();
          this.writeUrlState();
          this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });

    window.addEventListener('popstate', () => {
      this.readUrlState();
      this.applyFilters();
    });
  }

  readUrlState() {
    const params = new URLSearchParams(window.location.search);
    const page = Number.parseInt(params.get(this.pageParam) || '1', 10);
    this.currentPage = page > 0 ? page : 1;
    this.searchQuery = (params.get(this.searchParam) || '').trim();
    if (this.searchInput) this.searchInput.value = this.searchQuery;
  }

  writeUrlState() {
    const params = new URLSearchParams(window.location.search);

    if (this.currentPage > 1) params.set(this.pageParam, String(this.currentPage));
    else params.delete(this.pageParam);

    if (this.searchQuery) params.set(this.searchParam, this.searchQuery);
    else params.delete(this.searchParam);

    const query = params.toString();
    const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState({}, '', nextUrl);
  }

  async init() {
    this.showLoading();
    this.clearStatus();

    try {
      this.documents = await this.fetchAllDocuments();
      this.productGroups = this.groupByProduct(this.documents);
      this.applyFilters();
    } catch (error) {
      console.error('[BrochuresDocuments]', error);
      this.showStatus(this.errorMessage);
      if (this.listEl) this.listEl.innerHTML = '';
      if (this.paginationEl) this.paginationEl.innerHTML = '';
    } finally {
      this.hideLoading();
    }
  }

  async fetchAllDocuments() {
    const documents = [];
    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const data = await this.queryDocuments(cursor);
      const connection = data?.metaobjects;
      if (!connection) break;

      connection.nodes.forEach((node) => {
        const parsed = this.parseDocument(node);
        if (parsed) documents.push(parsed);
      });

      hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      cursor = connection.pageInfo?.endCursor || null;
    }

    return documents;
  }

  async queryDocuments(cursor) {
    const query = `
      query Documents($type: String!, $first: Int!, $after: String) {
        metaobjects(type: $type, first: $first, after: $after) {
          nodes {
            id
            handle
            file: field(key: "file") {
              references(first: 25) {
                nodes {
                  ... on GenericFile {
                    id
                    url
                    alt
                  }
                  ... on MediaImage {
                    id
                    alt
                    image { url }
                  }
                }
              }
            }
            products: field(key: "products") {
              references(first: 50) {
                nodes {
                  ... on Product {
                    id
                    title
                    handle
                    onlineStoreUrl
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    const response = await fetch(`https://${this.shop}/api/${this.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.token,
      },
      body: JSON.stringify({
        query,
        variables: {
          type: this.metaobjectType,
          first: 50,
          after: cursor,
        },
      }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(
          'Storefront API HTTP 401: invalid Public access token. Copy the Public token from Headless > Storefront API (not Admin GraphiQL).'
        );
      }
      throw new Error(`Storefront API HTTP ${response.status}`);
    }

    const json = await response.json();
    if (json.errors?.length) {
      throw new Error(json.errors.map((item) => item.message).join('; '));
    }

    return json.data;
  }

  parseDocument(node) {
    if (!node) return null;

    const fileNodes = node.file?.references?.nodes || [];
    const files = fileNodes
      .map((fileRef) => {
        const url = fileRef?.url || fileRef?.image?.url || '';
        if (!url) return null;
        const fileName = this.fileNameFromUrl(url);
        const alt = (fileRef?.alt || '').trim() || fileName;
        return {
          url,
          alt,
          fileName,
          label: this.downloadLabel || 'View document',
        };
      })
      .filter(Boolean);

    if (!files.length) return null;

    const products = (node.products?.references?.nodes || [])
      .filter((product) => product && product.title)
      .map((product) => ({
        id: product.id,
        title: product.title,
        handle: product.handle,
        url: product.onlineStoreUrl || (product.handle ? `/products/${product.handle}` : ''),
      }));

    return {
      id: node.id,
      handle: node.handle,
      files,
      products,
    };
  }

  fileNameFromUrl(url) {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const raw = decodeURIComponent(path.split('/').pop() || '');
      return raw.split('?')[0] || '';
    } catch (_error) {
      const raw = decodeURIComponent(String(url).split('/').pop() || '');
      return raw.split('?')[0] || '';
    }
  }

  groupByProduct(documents) {
    const groupsMap = new Map();

    documents.forEach((doc) => {
      if (!doc.products.length) {
        const key = '__unassigned__';
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            id: key,
            title: this.unassignedLabel,
            url: '',
            documents: [],
          });
        }
        groupsMap.get(key).documents.push(doc);
        return;
      }

      doc.products.forEach((product) => {
        const key = product.id;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            id: key,
            title: product.title,
            url: product.url,
            documents: [],
          });
        }
        groupsMap.get(key).documents.push(doc);
      });
    });

    return Array.from(groupsMap.values()).sort((a, b) => {
      if (a.id === '__unassigned__') return 1;
      if (b.id === '__unassigned__') return -1;
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    });
  }

  applyFilters() {
    const query = this.searchQuery.toLowerCase();

    if (!query) {
      this.filteredGroups = this.productGroups.slice();
    } else {
      this.filteredGroups = this.productGroups
        .map((group) => {
          const productMatch = group.title.toLowerCase().includes(query);
          const matchedDocs = group.documents.filter((doc) => {
            const fileText = (doc.files || [])
              .map((file) => `${file.alt} ${file.fileName}`)
              .join(' ');
            const haystack = fileText.toLowerCase();
            return haystack.includes(query);
          });

          if (productMatch) return { ...group, documents: group.documents.slice() };
          if (matchedDocs.length) return { ...group, documents: matchedDocs };
          return null;
        })
        .filter(Boolean);
    }

    const totalPages = Math.max(1, Math.ceil(this.filteredGroups.length / this.perPage));
    if (this.currentPage > totalPages) this.currentPage = totalPages;

    this.render();
  }

  render() {
    if (!this.listEl) return;

    if (!this.filteredGroups.length) {
      this.listEl.innerHTML = '';
      if (this.paginationEl) this.paginationEl.innerHTML = '';
      this.updateCount(0);
      this.showStatus(this.emptyMessage);
      return;
    }

    this.clearStatus();

    const start = (this.currentPage - 1) * this.perPage;
    const pageGroups = this.filteredGroups.slice(start, start + this.perPage);

    this.listEl.innerHTML = pageGroups.map((group) => this.renderGroup(group)).join('');
    this.renderPagination();
    this.updateCount(this.filteredGroups.length);
  }

  renderGroup(group) {
    const docs = group.documents
      .flatMap((doc) => {
        const files = doc.files || [];
        return files.map((file) => {
          return `
          <li class="bd-doc">
            <a class="bd-doc__title" href="${this.escapeAttr(file.url)}" target="_blank" rel="noopener noreferrer">
              ${this.escapeHtml(file.label || this.downloadLabel || 'View document')}
            </a>
          </li>
        `;
        });
      })
      .join('');

    return `
      <details class="bd-group">
        <summary class="bd-group__summary">
          <span class="bd-group__icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 16.5A4.5 4.5 0 0 0 17.5 8h-.7A6.5 6.5 0 0 0 4.5 10.5 4 4 0 0 0 5 18.5h14.5"></path>
              <path d="M12 12v7"></path>
              <path d="m8.5 16.5 3.5 3.5 3.5-3.5"></path>
            </svg>
          </span>
          <span class="bd-group__title">${this.escapeHtml(group.title)}</span>
          <span class="bd-group__chevron" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
              <path d="m6 9 6 6 6-6"></path>
            </svg>
          </span>
        </summary>
        <ul class="bd-group__docs" role="list">
          ${docs}
        </ul>
      </details>
    `;
  }

  renderPagination() {
    if (!this.paginationEl) return;

    const totalPages = Math.ceil(this.filteredGroups.length / this.perPage);
    if (totalPages <= 1) {
      this.paginationEl.innerHTML = '';
      return;
    }

    const items = [];

    if (this.currentPage > 1) {
      items.push(
        `<li><button type="button" class="bd-page bd-page--nav" data-bd-page="${this.currentPage - 1}">${this.escapeHtml(this.previousLabel)}</button></li>`
      );
    }

    for (let page = 1; page <= totalPages; page += 1) {
      if (page === this.currentPage) {
        items.push(`<li><span class="bd-page bd-page--current" aria-current="page">${page}</span></li>`);
      } else {
        items.push(`<li><button type="button" class="bd-page" data-bd-page="${page}">${page}</button></li>`);
      }
    }

    if (this.currentPage < totalPages) {
      items.push(
        `<li><button type="button" class="bd-page bd-page--nav" data-bd-page="${this.currentPage + 1}">${this.escapeHtml(this.nextLabel)}</button></li>`
      );
    }

    this.paginationEl.innerHTML = `<ul class="bd-pagination__list" role="list">${items.join('')}</ul>`;
  }

  updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = `${count} ${this.resultsLabel}`;
    this.countEl.hidden = count < 1;
  }

  showLoading() {
    if (this.loadingEl) this.loadingEl.hidden = false;
  }

  hideLoading() {
    if (this.loadingEl) this.loadingEl.hidden = true;
  }

  showStatus(message) {
    if (!this.statusEl) return;
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
  }

  clearStatus() {
    if (!this.statusEl) return;
    this.statusEl.hidden = true;
    this.statusEl.textContent = '';
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeAttr(value) {
    return this.escapeHtml(value);
  }
}

function initBrochuresDocuments(scope = document) {
  scope.querySelectorAll('[data-bd-root]').forEach((root) => {
    if (root.dataset.bdInitialized === 'true') return;
    root.dataset.bdInitialized = 'true';
    new BrochuresDocuments(root);
  });
}

document.addEventListener('DOMContentLoaded', () => initBrochuresDocuments());
document.addEventListener('shopify:section:load', (event) => initBrochuresDocuments(event.target));